import type { SxProps } from '@mui/material'
import Box from '@mui/material/Box'
import Menu from '@mui/material/Menu'
import MuiMenuItem from '@mui/material/MenuItem'
import * as React from 'react'
import { ReactElement, ReactNode, useEffect } from 'react'

import { ButtonSize } from './ButtonNew'
import IconButton from './IconButton'

const IconButtonDropdownNew = ({
    icon,
    id,
    children,
    closeOnClick = true,
    menuAutoWidth,
    sx,
    position = 'left',
    onToggle,
    className,
    size = 'sm',
}: {
    icon: JSX.Element
    id?: string
    closeOnClick?: boolean
    menuAutoWidth?: boolean
    sx?: SxProps
    position?: 'left' | 'right' | 'center'
    children: ReactNode
    onToggle?: (isOpen: boolean) => void
    className?: string
    size?: ButtonSize
}) => {
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null)
    const open = Boolean(anchorEl)

    const handleClick = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget)
    }
    const handleClose = () => {
        setAnchorEl(null)
    }
    const handlePanelClick = () => {
        if (closeOnClick) handleClose()
    }

    useEffect(() => {
        if (open) {
            onToggle?.(true)
        } else {
            // using timeout to match the dropdown closing transition time
            setTimeout(() => {
                onToggle?.(false)
            }, 200)
        }
    }, [open])

    return (
        <>
            <IconButton
                style={'floating'}
                aria-controls={open ? `dropdown-panel-${id}` : undefined}
                aria-haspopup="true"
                aria-expanded={open ? 'true' : undefined}
                leadingIcon={icon}
                onClick={handleClick}
                size={size}
                className={className}
                sx={{
                    color: `Grey900`,
                    ...sx,
                }}
            />
            <Menu
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: position,
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: position,
                }}
                onClick={handlePanelClick}
                MenuListProps={{
                    'aria-labelledby': `dropdown-btn-${id}`,
                }}
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                elevation={4}
                sx={{
                    '.MuiPaper-root': {
                        borderRadius: '8px',
                        minWidth: menuAutoWidth ? 'auto' : '280px',
                    },
                }}
            >
                {children}
            </Menu>
        </>
    )
}

const MenuItem = ({
    icon,
    title,
    onClick,
    disabled,
    labelSx,
}: {
    icon: ReactElement
    title: ReactNode
    onClick: () => void
    disabled?: boolean
    labelSx?: SxProps
}) => {
    return (
        <MuiMenuItem
            sx={{
                py: 1.5,
                color: `Grey900`,
                fontSize: '14px',
                '&:hover': {
                    backgroundColor: `Grey100`,
                },
            }}
            onClick={onClick}
            disabled={disabled}
        >
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                }}
            >
                <Box
                    sx={{
                        width: `24px`,
                        height: `24px`,
                        mr: 2,
                        color: `TextSecondary`,
                    }}
                >
                    {icon}
                </Box>
                <Box
                    sx={{
                        color: `Grey900`,
                        fontSize: '14px',
                        ...labelSx,
                    }}
                >
                    {title}
                </Box>
            </Box>
        </MuiMenuItem>
    )
}

IconButtonDropdownNew.MenuItem = MenuItem
export default IconButtonDropdownNew
